import { fetchAPI } from "./utils";

const SW_VERSION = process.env.REACT_APP_SW_VERSION || "2026-06-12";

function buildVersionedServiceWorkerUrl() {
	return `/service-worker.js?v=${encodeURIComponent(SW_VERSION)}`;
}

function isVersionedScriptUrl(scriptUrl) {
	if (!scriptUrl) return false;
	return scriptUrl.includes(`v=${encodeURIComponent(SW_VERSION)}`);
}

function urlBase64ToUint8Array(base64String) {
	const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
	const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
	const rawData = window.atob(base64);
	const outputArray = new Uint8Array(rawData.length);
	for (let i = 0; i < rawData.length; i += 1) {
		outputArray[i] = rawData.charCodeAt(i);
	}
	return outputArray;
}

export function isPushSupported() {
	if (typeof window === "undefined") return false;
	return (
		"serviceWorker" in navigator &&
		"PushManager" in window &&
		"Notification" in window
	);
}

async function getLocalPushSubscription() {
	if (!isPushSupported()) return null;

	try {
		const registration = await navigator.serviceWorker.getRegistration("/");
		if (!registration) return null;
		return await registration.pushManager.getSubscription();
	} catch {
		return null;
	}
}

export async function hasLocalPushSubscription() {
	if (!isPushSupported()) return false;
	if (Notification.permission !== "granted") return false;
	const subscription = await getLocalPushSubscription();
	return !!subscription;
}

function delay(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

// Safari/iOS PWA intermittently returns "Load failed" on the first network
// requests right after a push subscribe. Retry with exponential backoff so the
// subscription reliably reaches the server (otherwise the local flag shows as
// enabled but no notifications ever arrive).
const PERSIST_BACKOFF_MS = [400, 1000, 2000];

async function persistSubscriptionWithRetry(subscriptionJson) {
	const payload = JSON.stringify({
		subscription: subscriptionJson,
	});
	const maxAttempts = PERSIST_BACKOFF_MS.length + 1;
	let lastError = null;
	for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
		try {
			await fetchAPI("/users/me/push-subscription", {
				method: "POST",
				body: payload,
			});
			lastError = null;
			break;
		} catch (error) {
			lastError = error;
			const isLastAttempt = attempt === maxAttempts - 1;
			if (!isFetchLikeNetworkError(error) || isLastAttempt) {
				break;
			}
			await delay(PERSIST_BACKOFF_MS[attempt]);
		}
	}
	if (lastError) {
		throw lastError;
	}
}

export async function syncLocalPushSubscriptionToServer() {
	const subscription = await getLocalPushSubscription();
	if (!subscription) {
		throw new Error("Nessuna subscription push locale disponibile");
	}

	await persistSubscriptionWithRetry(subscription.toJSON());
	return subscription;
}

function isFetchLikeNetworkError(error) {
	const msg = String(error?.message || "").toLowerCase();
	return (
		msg.includes("failed to fetch") ||
		msg.includes("load failed") ||
		msg.includes("networkerror")
	);
}

async function ensureServiceWorkerRegistration() {
	if (!("serviceWorker" in navigator)) {
		throw new Error("Service Worker non supportato");
	}

	let registration = await navigator.serviceWorker.getRegistration("/");
	if (registration?.active?.scriptURL) {
		const isAlreadyVersioned = isVersionedScriptUrl(
			registration.active.scriptURL,
		);
		if (!isAlreadyVersioned) {
			await registration.unregister();
			registration = null;
		}
	}

	if (!registration) {
		registration = await navigator.serviceWorker.register(
			buildVersionedServiceWorkerUrl(),
			{
				updateViaCache: "none",
			},
		);
	}

	registration.update().catch(() => {});

	await navigator.serviceWorker.ready;
	return registration;
}

export async function subscribeUserToPush() {
	if (!isPushSupported()) {
		throw new Error("Push non supportate su questo dispositivo/browser");
	}

	// IMPORTANT: request the permission FIRST, synchronously within the user
	// gesture. On Safari/iOS (PWA) the native permission prompt must be invoked
	// before any awaited async work (e.g. fetch) that consumes the transient
	// user activation, otherwise it fails or never shows.
	const permission = await Notification.requestPermission();
	if (permission !== "granted") {
		throw new Error("Permesso notifiche non concesso");
	}

	const config = await fetchAPI("/users/me/push-config");
	if (!config?.push_available || !config?.vapid_public_key) {
		throw new Error("Push non configurate sul server");
	}

	const registration = await ensureServiceWorkerRegistration();
	let subscription = await registration.pushManager.getSubscription();
	if (!subscription) {
		const appServerKey = urlBase64ToUint8Array(config.vapid_public_key);
		try {
			subscription = await registration.pushManager.subscribe({
				userVisibleOnly: true,
				applicationServerKey: appServerKey,
			});
		} catch (error) {
			if (!isFetchLikeNetworkError(error)) {
				throw error;
			}
			// Safari/PWA can intermittently fail the first subscribe call.
			await registration.update().catch(() => {});
			subscription = await registration.pushManager.subscribe({
				userVisibleOnly: true,
				applicationServerKey: appServerKey,
			});
		}
	}

	await persistSubscriptionWithRetry(subscription.toJSON()).catch((error) => {
		// The browser subscription is already created at this point; a transient
		// Safari/iOS "Load failed" on the save must not surface as a hard error.
		// usePushOptIn re-syncs the local subscription on the next app load, so
		// notifications still get delivered. Only re-throw genuine (non-network)
		// failures so real misconfigurations remain visible.
		if (!isFetchLikeNetworkError(error)) {
			throw error;
		}
	});

	return subscription;
}

export async function unsubscribeUserFromPush() {
	if (!("serviceWorker" in navigator)) return;

	const registration = await navigator.serviceWorker.getRegistration("/");
	if (!registration) return;

	const subscription = await registration.pushManager.getSubscription();
	if (!subscription) return;

	const endpoint = subscription.endpoint;
	await subscription.unsubscribe();
	await fetchAPI("/users/me/push-subscription", {
		method: "DELETE",
		body: JSON.stringify({ endpoint }),
	});
}
